// v0.8 pieces used on several pages: rarity labels, the new-title popup, the auction banner,
// and the "send cash / send an item / propose a trade" windows between real players.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { api } from '../api.ts';
import { useGame, money, fmt } from '../state.tsx';
import { Loading, ItemIcon, ItemName } from './ui.tsx';
import { sequence } from '../fx/sound.ts';
import { fx } from '../fx/fx.tsx';

export const RARITY_LABEL: Record<string, string> = {
  common: 'Common', uncommon: 'Uncommon', rare: 'Rare', epic: 'Epic', legendary: 'Legendary', unique: 'One of a Kind',
};

/** A small coloured label: "Rare", "Legendary", "One of a Kind". */
export function RarityTag({ rarity }: { rarity: string }) {
  if (!rarity || rarity === 'common') return null;
  return <span className={`rarity-tag rt-${rarity}`}>{RARITY_LABEL[rarity] ?? rarity}</span>;
}

/** Ticks once a second so countdowns stay live. */
export function useTick(on = true) {
  const [, set] = useState(0);
  useEffect(() => {
    if (!on) return;
    const t = setInterval(() => set((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [on]);
}

export function countdown(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  if (h >= 48) return `${Math.floor(h / 24)} days`;
  return h ? `${h}:${String(m).padStart(2, '0')}:${String(ss).padStart(2, '0')}` : `${m}:${String(ss).padStart(2, '0')}`;
}

// ---------------------------------------------------------------- new titles
export function TitlePopup() {
  const { me, act } = useGame();
  const list = me?.newTitles ?? [];
  const shown = useRef('');
  useEffect(() => {
    const key = list.map((t: any) => t.id).join(',');
    if (!key || key === shown.current) return;
    shown.current = key;
    sequence([[0, 'medal'], [250, 'horn']]);
    fx.stamp('New title', `“${list[list.length - 1].name}”`, 'gold', 200);
  }, [list.length]);
  if (!list.length) return null;
  return (
    <div className="ach-popup title-popup">
      <div className="ach-badge medal-drop">T</div>
      <div className="grow">
        {list.map((t: any) => (
          <div key={t.id} style={{ marginBottom: 2 }}>
            <b className="gold">New title earned: “{t.name}”</b>
            <div className="small">Why: {t.how}</div>
          </div>
        ))}
        <a className="small" href="#/customize">Wear it on your profile</a> · <a className="small" href="#/achievements/titles">All titles</a>
      </div>
      <button className="btn small" onClick={() => act('/api/titles/seen')}>OK</button>
    </div>
  );
}

// ---------------------------------------------------------------- the auction banner (Friday evenings)
export function AuctionBanner() {
  const { me, byId } = useGame();
  const a = me?.houseAuction;
  useTick(!!a);
  if (!a) return null;
  const now = Date.now();
  const soon = a.status === 'upcoming' && a.opensAt - now < 3 * 3_600_000 && a.opensAt > now;
  if (a.status !== 'open' && !soon) return null;
  const item = byId.items.get(a.itemId);
  return (
    <div className={`auction-banner ${a.status}`}>
      <span className="auction-tag">Auction</span>
      <ItemIcon itemId={a.itemId} size={30} />
      <span className="grow">
        {a.status === 'open'
          ? <>The house is auctioning off <b>{item?.name}</b>. {a.topBid ? <>Top bid <span className="money">{money(a.topBid)}</span>{a.mine && <b className="good"> (you)</b>}.</> : 'No bids yet.'}</>
          : <><b>{item?.name}</b> goes on the block at 5pm. Get your cash ready.</>}
      </span>
      <span className="auction-clock">{countdown((a.status === 'open' ? a.endsAt : a.opensAt) - now)}</span>
      <a className="btn small" href="#/inventory/auction">{a.status === 'open' ? 'Bid' : 'Preview'}</a>
    </div>
  );
}

// ---------------------------------------------------------------- gifts and trades
function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  return (
    <div className="modal-back" onClick={onClose}>
      <div className="modal give-modal" role="dialog" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">{title}</div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}

export function SendCash({ to, onClose }: { to: { id: number; name: string }; onClose: () => void }) {
  const { me, act } = useGame();
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const n = Math.floor(Number(amount.replace(/[^0-9]/g, '')));
  const send = async () => {
    const r = await act('/api/give/cash', { playerId: to.id, amount: n, note });
    if (!r) return;
    sequence([[0, 'envelope'], [250, 'coins']]);
    fx.coins(14);
    fx.float(`−${money(n)}`, 'loss');
    fx.stamp('Sent', `${money(n)} to ${to.name}`, 'good');
    onClose();
  };
  return (
    <Modal title={`Send cash to ${to.name}`} onClose={onClose}>
      <p style={{ marginTop: 0 }}>Straight from your cash on hand (you have <span className="money">{money(me.cash)}</span>). They get it right away.</p>
      <div className="form-row"><label htmlFor="give-amount">Amount $</label><input id="give-amount" value={amount} placeholder="10000" onChange={(e) => setAmount(e.target.value)} /></div>
      <div className="row" style={{ gap: 4, marginBottom: 8, flexWrap: 'wrap' }}>
        {[1_000, 10_000, 100_000, 1_000_000].map((v) => <button key={v} className="btn small gray" onClick={() => setAmount(String(v))}>{money(v)}</button>)}
        <button className="btn small gray" onClick={() => setAmount(String(me.cash))}>All</button>
      </div>
      <div className="form-row"><label htmlFor="give-note">Note (optional)</label><input id="give-note" value={note} maxLength={120} onChange={(e) => setNote(e.target.value)} placeholder="For the wedding" /></div>
      <div className="right"><a className="small" onClick={onClose} style={{ marginRight: 12 }}>Cancel</a><button className={`btn ${n >= 1 && n <= me.cash ? '' : 'disabled'}`} onClick={send}>Send {n ? money(n) : ''}</button></div>
    </Modal>
  );
}

function ItemPicker({ items, value, onChange }: { items: { itemId: string; qty: number }[]; value: Record<string, number>; onChange: (v: Record<string, number>) => void }) {
  const { byId } = useGame();
  const sorted = [...items].filter((i) => byId.items.get(i.itemId)).sort((a, b) => {
    const A = byId.items.get(a.itemId); const B = byId.items.get(b.itemId);
    return B.attack + B.defense - (A.attack + A.defense);
  });
  if (!sorted.length) return <div className="muted small">No items.</div>;
  return (
    <div className="item-picker">
      {sorted.map((i) => {
        const n = value[i.itemId] ?? 0;
        return (
          <div key={i.itemId} className={`pick-row ${n ? 'on' : ''}`}>
            <ItemIcon itemId={i.itemId} size={30} />
            <span className="grow"><ItemName itemId={i.itemId} /> <span className="small dim">×{fmt(i.qty)}</span></span>
            <input aria-label={`How many ${byId.items.get(i.itemId).name}`} value={n || ''} placeholder="0" style={{ width: 46 }}
              onChange={(e) => {
                const q = Math.max(0, Math.min(i.qty, Math.floor(Number(e.target.value) || 0)));
                const next = { ...value };
                if (q) next[i.itemId] = q; else delete next[i.itemId];
                onChange(next);
              }} />
          </div>
        );
      })}
    </div>
  );
}

export function SendItem({ to, onClose }: { to: { id: number; name: string }; onClose: () => void }) {
  const { act, byId } = useGame();
  const [mine, setMine] = useState<any>(null);
  const [pick, setPick] = useState<Record<string, number>>({});
  useEffect(() => { api.get('/api/inventory').then((d) => setMine(d.items)).catch(() => {}); }, []);
  const chosen = Object.entries(pick);
  const send = async () => {
    for (const [itemId, qty] of chosen) {
      const r = await act('/api/give/item', { playerId: to.id, itemId, qty });
      if (!r) return;
    }
    sequence([[0, 'envelope'], [300, 'medal']]);
    fx.stamp('Delivered', `${chosen.map(([id, q]) => `${q > 1 ? `${q} × ` : ''}${byId.items.get(id).name}`).join(', ')} to ${to.name}`, 'good');
    onClose();
  };
  return (
    <Modal title={`Give items to ${to.name}`} onClose={onClose}>
      <p style={{ marginTop: 0 }}>Pick what to hand over. It goes into their inventory right away.</p>
      {!mine ? <Loading /> : <ItemPicker items={mine} value={pick} onChange={setPick} />}
      <div className="right" style={{ marginTop: 8 }}><a className="small" onClick={onClose} style={{ marginRight: 12 }}>Cancel</a><button className={`btn ${chosen.length ? '' : 'disabled'}`} onClick={send}>Give {chosen.length ? `${chosen.reduce((s, [, q]) => s + q, 0)} item(s)` : ''}</button></div>
    </Modal>
  );
}

export function ProposeTrade({ to, onClose, onDone }: { to: { id: number; name: string }; onClose: () => void; onDone?: () => void }) {
  const { act, me } = useGame();
  const [mine, setMine] = useState<any>(null);
  const [theirs, setTheirs] = useState<any>(null);
  const [give, setGive] = useState<Record<string, number>>({});
  const [want, setWant] = useState<Record<string, number>>({});
  const [giveCash, setGiveCash] = useState('');
  const [wantCash, setWantCash] = useState('');
  const [note, setNote] = useState('');
  useEffect(() => {
    api.get('/api/inventory').then((d) => setMine(d.items)).catch(() => {});
    api.get(`/api/players/${to.id}/items`).then((d) => setTheirs(d.items)).catch(() => setTheirs([]));
  }, [to.id]);
  const gc = Math.floor(Number(giveCash.replace(/[^0-9]/g, '')) || 0);
  const wc = Math.floor(Number(wantCash.replace(/[^0-9]/g, '')) || 0);
  const ok = (Object.keys(give).length || gc) && (Object.keys(want).length || wc) && gc <= me.cash;
  const send = async () => {
    const r = await act('/api/trades/propose', { toId: to.id, giveItems: give, giveCash: gc, wantItems: want, wantCash: wc, note });
    if (!r) return;
    sequence([[0, 'envelope'], [350, 'stamp']]);
    fx.stamp('Offer sent', `to ${to.name}`, 'gold');
    onDone?.();
    onClose();
  };
  return (
    <Modal title={`Trade with ${to.name}`} onClose={onClose}>
      <p style={{ marginTop: 0 }} className="small">The game holds what you offer until {to.name} answers (up to two days). If they say no, or don’t answer, it all comes back.</p>
      <div className="trade-sides">
        <div>
          <h3 className="trade-h">You give</h3>
          {!mine ? <Loading /> : <ItemPicker items={mine} value={give} onChange={setGive} />}
          <div className="form-row" style={{ marginTop: 6 }}><label htmlFor="tr-gc">Plus cash $</label><input id="tr-gc" value={giveCash} placeholder="0" onChange={(e) => setGiveCash(e.target.value)} /></div>
        </div>
        <div>
          <h3 className="trade-h">You get</h3>
          {!theirs ? <Loading /> : <ItemPicker items={theirs} value={want} onChange={setWant} />}
          <div className="form-row" style={{ marginTop: 6 }}><label htmlFor="tr-wc">Plus cash $</label><input id="tr-wc" value={wantCash} placeholder="0" onChange={(e) => setWantCash(e.target.value)} /></div>
        </div>
      </div>
      <div className="form-row"><label htmlFor="tr-note">Note (optional)</label><input id="tr-note" value={note} maxLength={120} onChange={(e) => setNote(e.target.value)} /></div>
      <div className="right"><a className="small" onClick={onClose} style={{ marginRight: 12 }}>Cancel</a><button className={`btn ${ok ? '' : 'disabled'}`} onClick={send}>Send the offer</button></div>
    </Modal>
  );
}

/** "Send cash · Give items · Trade" buttons for a real player's profile. */
export function GiveButtons({ to }: { to: { id: number; name: string } }) {
  const [open, setOpen] = useState<'' | 'cash' | 'item' | 'trade'>('');
  return (
    <>
      <button className="btn green" onClick={() => setOpen('cash')}>Send Cash</button>{' '}
      <button className="btn gray" onClick={() => setOpen('item')}>Give Items</button>{' '}
      <button className="btn gray" onClick={() => setOpen('trade')}>Trade</button>
      {open === 'cash' && <SendCash to={to} onClose={() => setOpen('')} />}
      {open === 'item' && <SendItem to={to} onClose={() => setOpen('')} />}
      {open === 'trade' && <ProposeTrade to={to} onClose={() => setOpen('')} />}
    </>
  );
}

/** A short list of items in a trade: "Golden Revolver, 2 × Crowbar + $50,000". */
export function TradeSide({ items, cash }: { items: Record<string, number>; cash: number }) {
  const entries = Object.entries(items);
  if (!entries.length && !cash) return <span className="dim">nothing</span>;
  return (
    <span className="trade-side">
      {entries.map(([id, n]) => <span key={id} className="trade-item"><ItemIcon itemId={id} size={22} /> {n > 1 && `${n} × `}<ItemName itemId={id} /></span>)}
      {cash > 0 && <span className="money"> {entries.length ? '+ ' : ''}{money(cash)}</span>}
    </span>
  );
}

